// frontend/src/components/MessageBox.jsx
// ═══════════════════════════════════════════════════════════════════════════
// MESSAGE BOX PERSONNALISÉ - FARM-BOOKING
// ═══════════════════════════════════════════════════════════════════════════
// Papy 73+ (Marine marchande, Normandie)
// Remplace les alert() et confirm() moches par des modales stylées
//
// USAGE :
//   import { useMessageBox, MessageBoxProvider } from '../components/MessageBox';
//   
//   const { showMessage, showConfirm } = useMessageBox();
//   
//   // Simple message
//   showMessage('success', 'Réservation enregistrée !');
//   
//   // Avec confirmation
//   const ok = await showConfirm('Voulez-vous supprimer ?', 'warning');
//   if (ok) { ... }
//
// ═══════════════════════════════════════════════════════════════════════════

import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, HelpCircle, Trash2, X } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES TYPES DE MESSAGES
// ═══════════════════════════════════════════════════════════════════════════

const MESSAGE_TYPES = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    iconColor: 'text-green-600',
    headerBg: 'bg-green-600',
    title: 'Succès'
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-red-100',
    borderColor: 'border-red-500',
    iconColor: 'text-red-600',
    headerBg: 'bg-red-600',
    title: 'Erreur'
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-500',
    iconColor: 'text-amber-600',
    headerBg: 'bg-amber-500',
    title: 'Attention'
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-500',
    iconColor: 'text-blue-600',
    headerBg: 'bg-blue-600',
    title: 'Information'
  },
  question: {
    icon: HelpCircle,
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-500',
    iconColor: 'text-purple-600',
    headerBg: 'bg-purple-600',
    title: 'Question'
  },
  delete: {
    icon: Trash2,
    bgColor: 'bg-red-100',
    borderColor: 'border-red-500',
    iconColor: 'text-red-600',
    headerBg: 'bg-red-600',
    title: 'Suppression'
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT (simple)
// ═══════════════════════════════════════════════════════════════════════════

const MessageBoxContext = createContext(null);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER - À mettre dans App.js
// ═══════════════════════════════════════════════════════════════════════════

export function MessageBoxProvider({ children }) {
  const [messageState, setMessageState] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    showCancel: false,
    onConfirm: null,
    onCancel: null
  });

  // Fermer la modale
  const closeMessage = useCallback(() => {
    setMessageState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Afficher un message simple (remplace alert)
  const showMessage = useCallback((type, message, title = null) => {
    return new Promise((resolve) => {
      const config = MESSAGE_TYPES[type] || MESSAGE_TYPES.info;
      setMessageState({
        isOpen: true,
        type,
        title: title || config.title,
        message,
        showCancel: false,
        onConfirm: () => {
          closeMessage();
          resolve(true);
        },
        onCancel: null
      });
    });
  }, [closeMessage]);

  // Afficher une confirmation (remplace confirm)
  const showConfirm = useCallback((message, type = 'question', title = null) => {
    return new Promise((resolve) => {
      const config = MESSAGE_TYPES[type] || MESSAGE_TYPES.question;
      setMessageState({
        isOpen: true,
        type,
        title: title || config.title,
        message,
        showCancel: true,
        onConfirm: () => {
          closeMessage();
          resolve(true);
        },
        onCancel: () => {
          closeMessage();
          resolve(false);
        }
      });
    });
  }, [closeMessage]);

  // Raccourcis pratiques
  const success = useCallback((message, title) => showMessage('success', message, title), [showMessage]);
  const error = useCallback((message, title) => showMessage('error', message, title), [showMessage]);
  const warning = useCallback((message, title) => showMessage('warning', message, title), [showMessage]);
  const info = useCallback((message, title) => showMessage('info', message, title), [showMessage]);
  const confirmDelete = useCallback((message, title) => showConfirm(message, 'delete', title || 'Confirmer la suppression'), [showConfirm]);

  const value = {
    showMessage,
    showConfirm,
    success,
    error,
    warning,
    info,
    confirmDelete
  };

  return (
    <MessageBoxContext.Provider value={value}>
      {children}
      
      {/* LA MODALE */}
      {messageState.isOpen && (
        <MessageBoxModal 
          {...messageState} 
          onClose={closeMessage}
        />
      )}
    </MessageBoxContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK - Pour utiliser dans les composants
// ═══════════════════════════════════════════════════════════════════════════

export function useMessageBox() {
  const context = useContext(MessageBoxContext);
  if (!context) {
    // Fallback si pas de Provider (utilise les alert/confirm natifs)
    return {
      showMessage: (type, message) => { alert(message); return Promise.resolve(true); },
      showConfirm: (message) => Promise.resolve(window.confirm(message)),
      success: (message) => { alert(message); return Promise.resolve(true); },
      error: (message) => { alert(message); return Promise.resolve(true); },
      warning: (message) => { alert(message); return Promise.resolve(true); },
      info: (message) => { alert(message); return Promise.resolve(true); },
      confirmDelete: (message) => Promise.resolve(window.confirm(message))
    };
  }
  return context;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT MODAL
// ═══════════════════════════════════════════════════════════════════════════

function MessageBoxModal({ type, title, message, showCancel, onConfirm, onCancel, onClose }) {
  const config = MESSAGE_TYPES[type] || MESSAGE_TYPES.info;
  const IconComponent = config.icon;

  // Fermer avec Escape
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showCancel && onCancel) {
          onCancel();
        } else if (onConfirm) {
          onConfirm();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showCancel, onCancel, onConfirm]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={showCancel ? onCancel : onConfirm}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[fadeIn_0.2s_ease-out]">
        
        {/* Header coloré */}
        <div className={`${config.headerBg} px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <IconComponent className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
          <button 
            onClick={showCancel ? onCancel : onConfirm}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Contenu */}
        <div className="p-6">
          <div className={`${config.bgColor} ${config.borderColor} border-l-4 rounded-lg p-4`}>
            <p className="text-gray-800 whitespace-pre-line">{message}</p>
          </div>
        </div>
        
        {/* Boutons */}
        <div className="px-6 pb-6 flex justify-end gap-3">
          {showCancel && (
            <button
              onClick={onCancel}
              className="px-5 py-2.5 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
          )}
          <button
            onClick={onConfirm}
            className={`px-5 py-2.5 rounded-lg font-medium text-white ${config.headerBg} hover:opacity-90 transition-opacity`}
          >
            {showCancel ? 'Confirmer' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT PAR DÉFAUT
// ═══════════════════════════════════════════════════════════════════════════

export default MessageBoxModal;